import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import api from "../lib/api";
import Reveal from "../components/Reveal";
import Marquee from "../components/Marquee";
import { ArrowRight, MapPin, CalendarDays, Instagram } from "lucide-react";
import TikTokIcon from "../components/TikTokIcon";

const INSTAGRAM_URL = "https://www.instagram.com/artemidefc";
const TIKTOK_URL = "https://www.tiktok.com/@artemide.fc";
const LIVE_URL = "https://www.tuttocampo.it/Lombardia/SecondaCategoria/GironeOMilano/Risultati";

const POSTS = [
  { platform: "Instagram", url: INSTAGRAM_URL, date: "13 Set 2026", text: "Esordio da sogno: Novella 0-4 Artemide. Che gruppo, che inizio." },
  { platform: "TikTok", url: TIKTOK_URL, date: "11 Set 2026", text: "Dietro le quinte: la vigilia del primo match di campionato." },
];

const HERO_IMG = "/hero-celebration.jpg";
const EASE = [0.22, 1, 0.36, 1];
const ESORDIO_CSI = new Date("2026-10-03T16:00:00");

function Countdown() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = ESORDIO_CSI - now;
  if (diff <= 0) return null;
  const cells = [
    [Math.floor(diff / 86400000), "Giorni"],
    [Math.floor((diff % 86400000) / 3600000), "Ore"],
    [Math.floor((diff % 3600000) / 60000), "Minuti"],
    [Math.floor((diff % 60000) / 1000), "Secondi"],
  ];
  return (
    <section className="border-y-4 border-gold bg-surface" data-testid="home-countdown">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 text-center">
        <p className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-gold mb-3">
          L'Esordio CSI · 3 Ottobre 2026 · Ore 16:00 · Centro Sportivo Enrico Cappelli
        </p>
        <h2 className="font-display font-black uppercase text-4xl sm:text-6xl text-white leading-none mb-10">
          La Seconda Squadra <span className="text-gold">Scende in Campo</span>
        </h2>
        <div className="grid grid-cols-4 gap-3 sm:gap-6 max-w-3xl mx-auto">
          {cells.map(([v, l]) => (
            <div key={l} className="grey-fade border border-line py-5 sm:py-7">
              <p className="font-display font-black text-5xl sm:text-8xl text-gold leading-none" data-testid={`countdown-${l.toLowerCase()}`}>
                {String(v).padStart(2, "0")}
              </p>
              <p className="font-mono text-[10px] sm:text-xs tracking-[0.25em] uppercase text-zinc-400 mt-2">{l}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function formatDate(d) {
  return new Date(d + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function Home() {
  const [nextMatch, setNextMatch] = useState(null);
  const [stats, setStats] = useState({ wins: 30, played: 91 });
  const [posts, setPosts] = useState(POSTS);
  const isMatchToday = nextMatch && nextMatch.date === new Date().toLocaleDateString("sv-SE");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    api.get("/matches").then((r) => {
      const upcoming = r.data
        .filter((m) => m.home_score === null && m.squad === "FIGC")
        .sort((a, b) => a.date.localeCompare(b.date));
      setNextMatch(upcoming[0] || null);
      const played = r.data.filter((m) => m.home_score !== null && m.home_score !== undefined);
      const wins = played.filter((m) => {
        const isHome = m.home_team.toLowerCase().includes("artemide");
        const ours = isHome ? m.home_score : m.away_score;
        const theirs = isHome ? m.away_score : m.home_score;
        return ours > theirs;
      }).length;
      setStats({ wins: 30 + wins, played: 91 + played.length });
    }).catch(() => {});
    api.get("/posts").then((r) => {
      if (Array.isArray(r.data) && r.data.length === 2) {
        setPosts(r.data.map((p) => ({
          platform: p.platform,
          url: p.platform === "Instagram" ? INSTAGRAM_URL : TIKTOK_URL,
          date: p.date,
          text: p.title,
        })));
      }
    }).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100vh] flex items-end overflow-hidden">
        <motion.img src={HERO_IMG} alt="Esultanza Artemide FC" style={{ y: heroImgY, scale: 1.18 }} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-night via-night/55 to-night/25" />
        <div className="absolute left-0 top-0 h-full w-3 diagonal-stripes opacity-90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pb-20 pt-40 w-full">
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-gold mb-6">
            Stagione 2026/27 · FIGC & CSI
          </motion.p>
          <h1 className="font-display font-black uppercase leading-[0.85] tracking-[0.02em] text-white text-[20vw] sm:text-[9rem] lg:text-[13rem]" data-testid="home-hero-title">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.15, ease: EASE }}>Artemide</motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span className="block text-gold" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.32, ease: EASE }}>FC</motion.span>
            </span>
          </h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6, ease: EASE }} className="mt-6 font-display uppercase text-2xl sm:text-4xl font-bold text-zinc-300 tracking-wide">
            · Dai Giovani per i Giovani ·
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.78, ease: EASE }} className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/squadre"
              data-testid="home-cta-squadre"
              className="bg-gold text-night font-display uppercase tracking-wider font-bold text-xl px-8 py-4 hover:bg-golddark transition-colors flex items-center gap-2"
            >
              Scopri le Squadre <ArrowRight size={20} />
            </Link>
            <Link
              to="/calendario"
              data-testid="home-cta-calendario"
              className="border-2 border-white/70 text-white font-display uppercase tracking-wider font-bold text-xl px-8 py-4 hover:border-gold hover:text-gold transition-colors"
            >
              Vedi il Calendario
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.95, ease: EASE }} className="mt-5 flex flex-wrap gap-4" data-testid="home-social-links">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="home-instagram-btn"
              className="flex items-center gap-2 border-2 border-gold text-gold font-display uppercase tracking-wider font-bold text-lg px-6 py-3 hover:bg-gold hover:text-night transition-colors"
            >
              <Instagram size={20} /> Instagram
            </a>
            <a
              href={TIKTOK_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="home-tiktok-btn"
              className="flex items-center gap-2 border-2 border-gold text-gold font-display uppercase tracking-wider font-bold text-lg px-6 py-3 hover:bg-gold hover:text-night transition-colors"
            >
              <TikTokIcon size={19} /> TikTok
            </a>
          </motion.div>
        </div>
      </section>

      <Countdown />

      {/* STATS BAND */}
      <section className="bg-gold text-night" data-testid="home-stats-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            [String(stats.played), "Partite Giocate"],
            [String(stats.wins), "Vittorie"],
            ["53", "Tesserati"],
            ["2", "Squadre"],
          ].map(([n, label]) => (
            <div key={label} className="text-center">
              <p className="font-display font-black text-6xl sm:text-7xl leading-none">{n}</p>
              <p className="font-mono text-xs tracking-[0.25em] uppercase mt-1 font-semibold">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* NEXT MATCH */}
      {nextMatch && (
        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 mt-24" data-testid="home-next-match">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-3">Prossima Partita</p>
          <div className="grey-fade border border-line border-l-4 border-l-gold p-8 sm:p-12">
            <div className="flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div>
                {isMatchToday && (
                  <a
                    href={LIVE_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="home-live-badge"
                    className="inline-flex items-center gap-2 bg-red-600 text-white font-mono text-xs font-semibold tracking-[0.2em] uppercase px-4 py-2 mb-4 mr-3 animate-pulse hover:bg-red-500 transition-colors"
                  >
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
                    </span>
                    LIVE · Segui la diretta
                  </a>
                )}
                <span className="inline-block bg-gold text-night font-mono text-xs font-semibold tracking-[0.2em] uppercase px-3 py-1 mb-4">
                  {nextMatch.squad === "FIGC" ? "Prima Squadra FIGC" : "Seconda Squadra CSI"}
                </span>
                <h2 className="font-display font-black uppercase text-4xl sm:text-6xl lg:text-7xl text-white leading-none">
                  {nextMatch.home_team}
                  <span className="block text-gold text-2xl sm:text-4xl my-2">VS</span>
                  {nextMatch.away_team}
                </h2>
              </div>
              <div className="text-zinc-300 space-y-3 shrink-0">
                <p className="flex items-center gap-3 font-display uppercase text-xl sm:text-2xl font-semibold">
                  <CalendarDays className="text-gold" size={24} />
                  {formatDate(nextMatch.date)}{nextMatch.time ? ` · ${nextMatch.time}` : ""}
                </p>
                <p className="flex items-center gap-3 font-display uppercase text-xl sm:text-2xl font-semibold">
                  <MapPin className="text-gold" size={24} />
                  {nextMatch.stadium}
                </p>
                <Link to="/calendario" data-testid="home-next-match-link" className="inline-flex items-center gap-2 text-gold font-display uppercase tracking-wider font-bold text-lg hover:underline">
                  Tutte le partite <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      )}

      {/* ULTIMI POST */}
      <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 mt-24" data-testid="home-latest-posts">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <h2 className="font-display font-black uppercase text-5xl sm:text-7xl text-white leading-none">
            Ultimi <span className="text-gold">Post</span>
          </h2>
          <div className="flex gap-3">
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="home-posts-instagram-link" className="flex items-center gap-2 text-gold font-display uppercase tracking-wider font-bold text-lg hover:underline">
              <Instagram size={18} /> Instagram
            </a>
            <a href={TIKTOK_URL} target="_blank" rel="noreferrer" data-testid="home-posts-tiktok-link" className="flex items-center gap-2 text-gold font-display uppercase tracking-wider font-bold text-lg hover:underline">
              <TikTokIcon size={17} /> TikTok
            </a>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {posts.map((p, i) => (
            <a
              key={i}
              href={p.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`home-post-card-${i}`}
              className="group grey-fade border border-line border-t-4 border-t-gold p-8 hover:bg-carddark transition-colors flex flex-col"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="flex items-center gap-2 bg-gold text-night font-mono text-[10px] font-semibold tracking-[0.2em] uppercase px-2 py-1">
                  {p.platform === "Instagram" ? <Instagram size={13} /> : <TikTokIcon size={12} />} {p.platform}
                </span>
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">{p.date}</span>
              </div>
              <p className="font-display font-semibold uppercase text-2xl sm:text-3xl text-white leading-tight flex-1">{p.text}</p>
              <p className="mt-6 flex items-center gap-2 text-gold font-display uppercase tracking-wider font-bold text-base">
                Vedi il post <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </p>
            </a>
          ))}
        </div>
      </Reveal>

      {/* SQUADS TEASER */}
      <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 mt-24" data-testid="home-squads-teaser">
        <h2 className="font-display font-black uppercase text-5xl sm:text-7xl text-white leading-none mb-10">
          Le Nostre <span className="text-gold">Squadre</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { n: "01", title: "Prima Squadra", badge: "FIGC", img: "/logo-crest.png", imgAlt: "Logo Prima Squadra FIGC", desc: "Il cuore pulsante del club: il campionato di Seconda Categoria FIGC.", testid: "home-squad-card-figc" },
            { n: "02", title: "Seconda Squadra", badge: "CSI", img: "/logo-csi.png", imgAlt: "Logo Seconda Squadra CSI", desc: "Giovani, talento e futuro nero-giallo nel campionato CSI.", testid: "home-squad-card-csi" },
          ].map((s) => (
            <Link
              key={s.badge}
              to="/squadre"
              data-testid={s.testid}
              className="group relative grey-fade border border-line p-10 overflow-hidden hover:border-gold transition-colors"
            >
              <span className="absolute -top-6 -right-2 font-display font-black text-[10rem] leading-none text-stroke-gold opacity-40 group-hover:opacity-100 transition-opacity">
                {s.n}
              </span>
              <img src={s.img} alt={s.imgAlt} className="h-20 w-20 object-contain mb-6 group-hover:scale-105 transition-transform" />
              <span className="inline-block bg-gold text-night font-mono text-xs font-semibold tracking-[0.2em] px-3 py-1 mb-6">{s.badge}</span>
              <h3 className="font-display font-black uppercase text-4xl sm:text-5xl text-white leading-none">{s.title}</h3>
              <p className="text-zinc-400 mt-4 max-w-sm">{s.desc}</p>
              <p className="mt-8 flex items-center gap-2 text-gold font-display uppercase tracking-wider font-bold text-lg">
                Vedi la rosa <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </p>
            </Link>
          ))}
        </div>
      </Reveal>

      <div className="mt-24">
        <Marquee />
      </div>
    </main>
  );
}
